import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  colors,
  fonts,
  radii,
  shadows,
  breakpoints,
  headingStyle,
  containerStyle,
  icones,
} from '../annuaire/designSystem';
import {
  RatingBadge,
  StatusBadge,
  TypologieBadge,
  ServicePill,
  BadgeBonusRepar,
} from '../annuaire/shared';
import { appliqueBonusRepar } from '../../annuaire/bonusRepar';
import {
  adresseMasquee,
  lieuApproximatif,
} from '../../annuaire/adressePublique';
import {
  AvisEtoiles,
  LigneAtelier,
  useStatutDuJour,
} from '../annuaire/shared/InfosAtelier';

// ---------------------------------------------------------------------------
// Styled Components
// ---------------------------------------------------------------------------

const HeroSection = styled.section`
  margin: 0;
  padding: 32px 0 40px;
`;

const Container = styled.div`
  ${containerStyle}
`;

const HeroCard = styled.div`
  background-color: ${colors.white};
  border-radius: 30px;
  overflow: hidden;
  box-shadow: ${shadows.card};
  display: grid;
  grid-template-columns: 1fr;

  @media (min-width: ${breakpoints.mobile}) {
    grid-template-columns: ${(p) =>
      p.$hasPhoto && !p.$empile ? '2fr 3fr' : '1fr'};
  }
`;

/*
 * Fiche d'un atelier Fixer Lite (lot 2) : la carte de rendez-vous à droite,
 * collante, et ce qui suit la tête (les prestations) sous elle, à gauche. Sur
 * mobile, dans l'ordre : la tête, la carte, la suite.
 */
const Grille = styled.div`
  display: grid;
  gap: 24px;
  grid-template-areas: 'tete' 'carte' 'suite';

  @media (min-width: 992px) {
    grid-template-columns: minmax(0, 1fr) 300px;
    grid-template-areas: 'tete carte' 'suite carte';
    gap: 24px 32px;
  }
`;

const Zone = styled.div`
  grid-area: ${(p) => p.$zone};
  min-width: 0;
`;

const ZoneCarte = styled(Zone)`
  @media (min-width: 992px) {
    align-self: start;
    position: sticky;
    /* Sous l'en-tête du site, collant lui aussi (80 px). */
    top: 100px;
  }
`;

const HeroImageCol = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeroImage = styled.div`
  background: linear-gradient(
    135deg,
    ${colors.vertPastel} 0%,
    ${colors.bleuPastel} 100%
  );
  min-height: 220px;
  flex: 1;

  @media (min-width: 992px) {
    min-height: ${(p) => (p.$empile ? '300px' : '220px')};
  }
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
`;

const HeroPhoto = styled.img`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  object-fit: cover;
`;

const HeroPlaceholder = styled.img`
  width: 80px;
  height: auto;
  opacity: 0.5;
`;

const PhotoGallery = styled.div`
  display: flex;
  gap: 6px;
  padding: 6px 0 0;
  background-color: ${colors.white};
  overflow: hidden;
`;

const GalleryThumb = styled.img`
  flex: 1;
  min-width: 0;
  height: 72px;
  object-fit: cover;
  border-radius: 8px;
  cursor: pointer;
  transition: opacity 0.2s ease;
  border: 2px solid ${(p) => (p.$active ? colors.bleu : 'transparent')};

  &:hover {
    opacity: 0.85;
  }

  @media (max-width: 767px) {
    height: 56px;
  }
`;

const HeroContent = styled.div`
  padding: 24px;
  font-family: ${fonts.body};

  @media (min-width: ${breakpoints.mobile}) {
    padding: 40px;
  }
`;

/* Tête d'une fiche Fixer Lite : les lignes d'infos du listing, serrées. */
const HeroContentEmpile = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 22px;
  font-family: ${fonts.body};
  font-size: 0.9rem;
  color: ${colors.textSecondary};

  /* Une règle globale impose « h1 { font-size: 2.5em !important } ». */
  h1 {
    margin: 0;
    font-size: 1.75rem !important;
    line-height: 1.15;
  }

  @media (min-width: 992px) {
    padding: 28px 32px;

    h1 {
      font-size: 2.1rem !important;
    }
  }
`;

const Adresse = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 7px;

  img {
    flex-shrink: 0;
    width: 16px;
    height: 18px;
    object-fit: contain;
    margin-top: 1px;
  }
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const BadgesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
`;

/* Le badge du Bonus Répar à côté de la note, sans prendre de place au-dessus
   du nom de l'atelier (retour de Clément du 14/10). */
const NoteEtBonus = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
`;

const Title = styled.h1`
  ${headingStyle}
  font-size: 1.75rem;
  margin-bottom: 16px;

  @media (min-width: ${breakpoints.mobile}) {
    font-size: 2.75rem;
  }
`;

const Address = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 24px;
  color: ${colors.textDark};
  font-size: 1.05rem;
  line-height: 1.5;
`;

const AddressIcon = styled.img`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  object-fit: contain;
  margin-top: 2px;
`;

const PillsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
`;

const ButtonsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (min-width: ${breakpoints.mobile}) {
    flex-direction: row;
  }
`;

const BtnPrimary = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 32px;
  border-radius: ${radii.card};
  font-weight: 600;
  font-size: 1rem;
  font-family: ${fonts.body};
  text-decoration: none;
  background-color: ${colors.vert};
  color: ${colors.bleuFonce};
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover,
  &:active,
  &:focus {
    color: ${colors.bleuFonce} !important;
    opacity: 1;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(213, 255, 79, 0.3);
  }
`;

const BtnSecondary = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 32px;
  border-radius: ${radii.card};
  font-weight: 600;
  font-size: 1rem;
  font-family: ${fonts.body};
  text-decoration: none;
  background-color: ${colors.bleuFonce};
  color: ${colors.white};
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover,
  &:active,
  &:focus {
    color: ${colors.white} !important;
    opacity: 0.9;
    transform: translateY(-2px);
  }
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * `carte` : sur la fiche d'un atelier qui prend ses rendez-vous en ligne
 * (Fixer Lite), la carte de rendez-vous porte le mode, les créneaux et les
 * boutons ; la tête n'a plus ni « Appeler » ni « Voir le site web » (le site
 * reste dans les infos pratiques). `children` : ce qui suit la tête dans la
 * colonne de gauche (les prestations).
 */
export default function ShopHero({ shop, carte = null, children = null }) {
  const details = shop.details || {};
  const seo = details.seo || {};
  const photos = details.photos || {};
  const mainPhoto = photos.s3_main || null;
  const galleryPhotos = photos.s3_urls || [];
  const services = details.services || [];

  const [activePhoto, setActivePhoto] = useState(mainPhoto);
  const bonus = appliqueBonusRepar(shop);

  // Re-sync activePhoto when shop data arrives (client-side fetch)
  useEffect(() => {
    if (mainPhoto) setActivePhoto(mainPhoto);
  }, [mainPhoto]);

  const h1 = seo.h1_page || shop.name || '';
  const noteGoogle = details.note_google;
  const nbAvis = details.nb_avis_google;
  const { typologie } = details;

  // FIXERLIGHT-068 : `shop` en second argument fait retomber statut ET
  // horaires du jour sur le gabarit Fixer Lite plutôt que sur les horaires
  // Google (vides pour ce type d'atelier), pour ne jamais contredire le
  // tableau d'horaires (ShopInfosPratiques) plus bas sur la même fiche.
  const { statut, horaires: horairesDuJour } = useStatutDuJour(details, shop);
  const phone = shop.phone || details.telephone_google || '';
  const website = shop.website || details.site_web || '';

  // Build address parts
  const addressParts = [];
  if (shop.address) {
    const parts = shop.address.split(/\d{5}/);
    if (parts[0]) addressParts.push(parts[0].trim());
  }
  const cityLine = [
    shop.postal_code,
    shop.city,
    details.departement,
    details.region,
  ]
    .filter(Boolean)
    .join(', ');

  // « 1 rue des Sœurs, 67000 Strasbourg », comme sur la carte du listing.
  // FIXERLIGHT-268 : sans adresse publiée, « 75020 Paris · <zone> ».
  const masquee = adresseMasquee(shop);
  const adresseCourte = masquee
    ? lieuApproximatif(shop)
    : [
        (addressParts[0] || '').replace(/,\s*$/, ''),
        [shop.postal_code, shop.city].filter(Boolean).join(' '),
      ]
        .filter(Boolean)
        .join(', ');

  const hasPhoto = !!(mainPhoto || galleryPhotos.length > 0);

  // Build thumbnails: main photo first, then gallery, deduplicated.
  // Cap matches the backoffice limit of 5 photos (1 principale + 4 secondaires).
  const allPhotos = [mainPhoto, ...galleryPhotos].filter(Boolean);
  const thumbs = [...new Set(allPhotos)].slice(0, 5);

  return (
    <HeroSection>
      <Container>
        {carte ? (
          <Grille>
            <Zone $zone="tete">{tete(true)}</Zone>
            <ZoneCarte $zone="carte">{carte}</ZoneCarte>
            <Zone $zone="suite">{children}</Zone>
          </Grille>
        ) : (
          tete(false)
        )}
      </Container>
    </HeroSection>
  );

  function tete(empile) {
    return (
      <HeroCard $hasPhoto={hasPhoto} $empile={empile}>
        {hasPhoto && (
          <HeroImageCol>
            <HeroImage $empile={empile}>
              {activePhoto ? (
                <HeroPhoto src={activePhoto} alt={shop.name || ''} />
              ) : (
                <HeroPlaceholder src={icones.velo} alt="Pas de photo" />
              )}
            </HeroImage>
            {thumbs.length > 1 && (
              <PhotoGallery>
                {thumbs.map((url, i) => (
                  <GalleryThumb
                    key={i}
                    src={url}
                    alt={`${shop.name || ''} - Photo ${i + 1}`}
                    $active={url === activePhoto}
                    loading="lazy"
                    onClick={() => setActivePhoto(url)}
                  />
                ))}
              </PhotoGallery>
            )}
          </HeroImageCol>
        )}

        {empile ? (
          <HeroContentEmpile>
            {bonus ? (
              <NoteEtBonus>
                <AvisEtoiles note={noteGoogle} nbAvis={nbAvis} />
                <BadgeBonusRepar />
              </NoteEtBonus>
            ) : (
              <AvisEtoiles note={noteGoogle} nbAvis={nbAvis} />
            )}
            <Title>{h1}</Title>
            <LigneAtelier
              typologie={typologie || details.typologie_etablissement}
              statut={statut}
              horaires={horairesDuJour}
            />
            {(shop.address || cityLine) && (
              <Adresse>
                <img src={icones.localisation} alt="" />
                <span>{adresseCourte}</span>
              </Adresse>
            )}
            {services.length > 0 && (
              <Tags>
                {services.map((svc, i) => (
                  <ServicePill key={i} name={svc} />
                ))}
              </Tags>
            )}
          </HeroContentEmpile>
        ) : (
          <HeroContent>
            <BadgesRow>
              <RatingBadge note={noteGoogle} count={nbAvis} />
              <StatusBadge statut={statut} />
              <TypologieBadge typologie={typologie} />
              {bonus && <BadgeBonusRepar />}
            </BadgesRow>

            <Title>{h1}</Title>

            {(shop.address || cityLine) && (
              <Address>
                <AddressIcon src={icones.localisation} alt="Localisation" />
                <div>
                  {masquee ? (
                    adresseCourte
                  ) : (
                    <>
                      {addressParts[0] && (
                        <>
                          {addressParts[0]}
                          <br />
                        </>
                      )}
                      {cityLine}
                    </>
                  )}
                </div>
              </Address>
            )}

            {services.length > 0 && (
              <PillsRow>
                {services.map((svc, i) => (
                  <ServicePill key={i} name={svc} />
                ))}
              </PillsRow>
            )}

            <ButtonsRow>
              {phone && (
                <BtnPrimary href={`tel:${phone}`}>
                  <img
                    src={icones.smartphone}
                    alt=""
                    style={{
                      height: 20,
                      width: 'auto',
                      objectFit: 'contain',
                    }}
                  />{' '}
                  Appeler
                </BtnPrimary>
              )}
              {website && (
                <BtnSecondary
                  href={website}
                  target="_blank"
                  rel="noopener noreferrer">
                  Voir le site web
                </BtnSecondary>
              )}
            </ButtonsRow>
          </HeroContent>
        )}
      </HeroCard>
    );
  }
}
